import React, { forwardRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { HeaderPositions, useHeader, } from '@polestar/component-warehouse-react/components/header/HeaderContext';
import { useMotionValue, useScroll, motion, useTransform } from 'framer-motion';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import MediaQueries from '@polestar/component-warehouse-react/tokens/media-queries/MediaQueries';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { HeaderVariants } from '@polestar/component-warehouse-react/components/header/Header';
import useOnValuesChange from '@polestar/component-warehouse-react/hooks/useOnValuesChange';
import { MegaMenuVariants } from '@polestar/component-warehouse-react/components/mega-menu/MegaMenu';
import { useCSSCustomPropWatch } from '@polestar/component-warehouse-react/hooks/useCSSCustomProp';
import { createCSSTransition, MotionDurations, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
const SideNavigationContainer = forwardRef(({ children, offset, desktopOnly, ...props }, ref) => {
    const [thresholdValue, setThresholdValue] = useState(32);
    const { safeAreaHeight, headerVisible, headerPosition, headerVariant, headerHeight, baseHeaderHeight, } = useHeader();
    const { scrollY } = useScroll();
    const safeAreaMotionValue = useMotionValue(undefined);
    const topOffset = useMotionValue('0px');
    const maxHeight = useMotionValue(`calc(100vh - ${safeAreaHeight || 0}px)`);
    const isMegaMenu = headerVariant === null ? 1 : 0;
    const megaMenuSafeAreaHeight = useCSSCustomPropWatch('[data-version]', '--cw-mega-menu-safe-area-height');
    const megaMenuBlockSize = useCSSCustomPropWatch('[data-version]', '--cw-mega-menu-block-size');
    const megaMenuVariant = useCSSCustomPropWatch('[data-version]', '--cw-internal-mega-menu-variant');
    const parseChanges = (scrollValue, headerVisibleValue, headerPositionValue, safeAreaValue, headerHeightValue, baseHeaderHeightValue) => {
        if (!isMegaMenu) {
            if (scrollValue > 0) {
                const safeArea = !headerVisibleValue ? 0 : safeAreaValue;
                const thresholdOffset = 
                // necessary during subNavigation pushing the SideNavigation more down
                safeAreaValue > thresholdValue
                    ? // determine when extra sub-navigation items exist to ensure that the SideNavigation will have spacing in between. when no sub-navgation items exist there is no need for extra spacing due to the initial spacing between the Header and the content (safeAreaHeight)
                        headerHeightValue > baseHeaderHeightValue
                            ? // Spacings.large is the Header bottom spacing
                                // TODO: this is responsive
                                `calc(${safeAreaValue}px + ${Spacings.large})`
                            : `${safeAreaValue}px`
                    : `${thresholdValue || 0}px`;
                if (headerVariant === HeaderVariants.static) {
                    topOffset.set(`${scrollValue > thresholdValue ? thresholdValue : scrollValue}px`);
                    if (scrollValue > safeAreaValue) {
                        // header is not in-view anymore
                        maxHeight.set(`calc(100vh - ${thresholdValue || 0}px)`);
                    }
                    else {
                        maxHeight.set(`calc(100vh - ${safeAreaValue - scrollValue || 0}px)`);
                    }
                }
                else {
                    if (headerPositionValue === HeaderPositions.fixed) {
                        if (safeArea - thresholdValue > scrollValue) {
                            // stay at the same position
                            if (headerVariant === HeaderVariants.alwaysVisible) {
                                topOffset.set(thresholdOffset);
                            }
                        }
                        else {
                            topOffset.set(thresholdOffset);
                            const maxHeightValue = `calc(100vh - ${headerVariant === HeaderVariants.alwaysVisible ||
                                (headerVariant === HeaderVariants.default && !!headerVisibleValue)
                                ? `${safeAreaValue}px`
                                : thresholdOffset || 0})`;
                            maxHeight.set(maxHeightValue);
                        }
                    }
                    else if (headerPositionValue === HeaderPositions.static) {
                        if (headerVariant !== HeaderVariants.alwaysVisible) {
                            // initial start value which is different for alwaysVisible
                            topOffset.set(`${thresholdValue}px`);
                        }
                        // not yet switched to fixed
                        const maxHeightValue = `calc(100vh - ${safeAreaValue -
                            scrollValue +
                            /* until threshold ensure that max-height adjusts correctly to be able to scroll to the end */
                            // TODO: where is 12px coming from? Header adjustment?
                            (scrollValue > thresholdValue ? thresholdValue + 12 : 0) || 0}px)`;
                        maxHeight.set(maxHeightValue);
                    }
                    else {
                        // reset to default when scrolling to the top
                        topOffset.set(`0px`);
                    }
                }
            }
            else {
                // initial
                maxHeight.set(`calc(100vh - ${safeAreaValue || 0}px)`);
            }
        }
    };
    useOnValuesChange([
        scrollY,
        headerVisible,
        headerPosition,
        safeAreaMotionValue,
        headerHeight,
        baseHeaderHeight,
    ], parseChanges);
    const parseMegaMenuChanges = (headerVisibleValue, megaMenuBlockSizeValue, megaMenuVariantValue) => {
        if (isMegaMenu) {
            let changeOffsetTo;
            if (headerVisibleValue &&
                megaMenuVariantValue === MegaMenuVariants.default &&
                !!megaMenuBlockSizeValue) {
                changeOffsetTo = createCustomSpacing(megaMenuBlockSizeValue.replace('px', '') / 16);
            }
            else {
                changeOffsetTo = createCustomSpacing(thresholdValue / 16);
            }
            topOffset.set(changeOffsetTo);
        }
    };
    useOnValuesChange([
        headerVisible,
        megaMenuBlockSize,
        megaMenuVariant,
    ], parseMegaMenuChanges);
    useEffect(() => {
        // adjust the thresholdValue with for example TableTop setups which do not use 1rem as the default fontSize
        const renderedThresholdValue = 2 /* rem */ * parseFloat(window.getComputedStyle(document.documentElement).fontSize);
        if (renderedThresholdValue !== thresholdValue) {
            setThresholdValue(2 * parseFloat(window.getComputedStyle(document.documentElement).fontSize));
        }
    }, [thresholdValue]);
    useEffect(() => {
        // sometimes the safeAreaHeight is later than the other values or those values keep the same so this is needed to ensure the right top offset
        safeAreaMotionValue.set(safeAreaHeight);
    }, [safeAreaHeight, safeAreaMotionValue]);
    const parsedTopOffset = useTransform([
        headerVisible,
        megaMenuVariant,
        megaMenuBlockSize,
    ], ([latestHeaderVisible, latestMegaMenuVariant, latestMegaMenuBlockSize]) => {
        if (latestHeaderVisible &&
            latestMegaMenuVariant === MegaMenuVariants.default &&
            !!latestMegaMenuBlockSize) {
            // make rem value
            return createCustomSpacing(Number(latestMegaMenuBlockSize.replace('px', '')) / 16);
        }
        return createCustomSpacing(thresholdValue / 16);
    });
    const parsedMaxHeight = useTransform([
        headerVisible,
        topOffset,
        megaMenuSafeAreaHeight,
    ], ([latestHeaderVisible, latestTopOffset, latestSafeAreaHeight]) => {
        if (isMegaMenu) {
            if (latestHeaderVisible) {
                return `calc(100vh - ${latestSafeAreaHeight || 0})`;
            }
            // append the minimal top offset / threshold
            return `calc(100vh - ${latestTopOffset} - ${latestSafeAreaHeight || 0})`;
        }
        return undefined;
    });
    return (React.createElement(StickyContainer, { ...props, ref: ref, offset: offset, style: {
            position: 'sticky',
            // Split up the rendering for Header or MegaMenu to prevent rendering issues
            top: isMegaMenu ? parsedTopOffset : topOffset,
            maxHeight: isMegaMenu ? parsedMaxHeight : maxHeight,
        }, desktopOnly: desktopOnly }, children));
});
SideNavigationContainer.displayName = 'SideNavigationContainer';
export default SideNavigationContainer;
export const StickyContainer = styled(motion.nav, shouldNotForwardProps(true, 'offset', 'desktopOnly'))(({ offset, desktopOnly }) => css(css `
      display: block;

      ${MediaQueries.desktop} {
        padding-inline-end: ${offset ? Spacings.xLarge : `0px`};
        overflow-y: auto;
      }
    `, desktopOnly &&
    css `
        transition: ${createCSSTransition('top', MotionEasings.default, MotionDurations.fast)};

        ${responsiveStyles({
        display: {
            mobile: 'none',
            tablet: 'none',
            desktop: 'block',
        },
    })}
      `));
